/**
 * MeasurementsSection — tabla de perímetros corporales y evolución.
 *
 * Render: una tabla A4-style donde las filas son medidas (Peso, Pecho…) y
 * las columnas son tomas de medida ("Inicial", "Mes 1"…). Todos los campos
 * editables inline.
 *
 * En modo editor:
 *   - "+ Columna" agrega una toma (push "" a cada row.values, push label a columns)
 *   - "+ Fila" agrega una MeasurementRow
 *   - ✕ para eliminar fila o columna
 * En `readonly` (vista pública / PDF): tabla limpia sin botones de edición.
 */
import { useEffect, useState } from "react";
import { genId, type MeasurementsData, type MeasurementRow } from "../../../types/plan";
import { MEASUREMENT_FIELDS, listMeasurements, type ClientMeasurement } from "../../../api/measurements";
import { Icon } from "../../../lib/Icon";
import { InlineEditable } from "../InlineEditable";
import { PageHeader, SectionPage } from "./section-shared";

interface MeasurementsSectionProps {
  data: MeasurementsData;
  onChange: (next: MeasurementsData) => void;
  readonly?: boolean;
  /** Si el plan está vinculado a un cliente, habilita "Importar del cliente"
   *  para traer tomas reales del historial. */
  clientId?: number | null;
}

/** Construye columnas + filas de la sección a partir de tomas reales del cliente
 *  (cronológico: la más vieja a la izquierda). Solo incluye filas con algún dato. */
function buildFromTakes(takes: ClientMeasurement[]): { columns: string[]; rows: MeasurementRow[] } {
  const columns = takes.map((m) => (m.measured_at || "").slice(0, 10) || "Toma");
  const rows = MEASUREMENT_FIELDS
    .map((f) => {
      const values = takes.map((m) => {
        const v = (m as unknown as Record<string, unknown>)[f.key as string];
        return v != null ? String(v) : "";
      });
      return { id: genId("mr"), label: f.label, unit: f.unit, values, _has: values.some((v) => v !== "") };
    })
    .filter((r) => r._has)
    .map(({ _has, ...r }) => { void _has; return r; });
  return { columns, rows };
}

export function MeasurementsSection({ data, onChange, readonly, clientId }: MeasurementsSectionProps) {
  const [importing, setImporting] = useState(false);
  const columns = data.columns || [];
  const rows = data.rows || [];

  const updateSummary = (v: string) => onChange({ ...data, summary: v });

  const updateColumn = (ci: number, label: string) => {
    const next = [...columns];
    next[ci] = label;
    onChange({ ...data, columns: next });
  };

  const updateRow = (ri: number, patch: Partial<MeasurementRow>) => {
    const next = rows.map((r, i) => (i === ri ? { ...r, ...patch } : r));
    onChange({ ...data, rows: next });
  };

  const updateValue = (ri: number, ci: number, v: string) => {
    const next = rows.map((r, i) => {
      if (i !== ri) return r;
      const values = [...(r.values || [])];
      values[ci] = v;
      return { ...r, values };
    });
    onChange({ ...data, rows: next });
  };

  const addColumn = () => {
    const nextCols = [...columns, `Toma ${columns.length + 1}`];
    const nextRows = rows.map((r) => ({ ...r, values: [...(r.values || []), ""] }));
    onChange({ ...data, columns: nextCols, rows: nextRows });
  };

  const removeColumn = (ci: number) => {
    if (!confirm(`¿Eliminar la columna "${columns[ci] || `Toma ${ci + 1}`}"?`)) return;
    const nextCols = columns.filter((_, i) => i !== ci);
    const nextRows = rows.map((r) => ({
      ...r,
      values: (r.values || []).filter((_, i) => i !== ci),
    }));
    onChange({ ...data, columns: nextCols, rows: nextRows });
  };

  const addRow = () => {
    const newRow: MeasurementRow = {
      id: genId("mr"),
      label: "Nueva medida",
      unit: "cm",
      values: columns.map(() => ""),
    };
    onChange({ ...data, rows: [...rows, newRow] });
  };

  const removeRow = (ri: number) => {
    if (!confirm(`¿Eliminar la fila "${rows[ri]?.label || ""}"?`)) return;
    onChange({ ...data, rows: rows.filter((_, i) => i !== ri) });
  };

  return (
    <SectionPage readonly={readonly}>
      <PageHeader eyebrow="Medidas y progreso" title="Medidas y progreso" />

      <p className="al-pg-summary">
        <InlineEditable
          value={data.summary || ""}
          onChange={updateSummary}
          multiline
          readonly={readonly}
          placeholder="Resumen / objetivo de seguimiento de medidas…"
          fieldPath="summary"
        />
      </p>

      <div className="al-meas-sec-scroll">
        <table className="al-pg-table al-meas-sec-table">
          <thead>
            <tr>
              <th>Medida</th>
              {columns.map((col, ci) => (
                <th key={ci} className="al-meas-sec-colh">
                  <span className="al-meas-sec-colh-inner">
                    <InlineEditable
                      value={col}
                      onChange={(v) => updateColumn(ci, v)}
                      readonly={readonly}
                      placeholder="Toma…"
                      fieldPath={`col.${ci}`}
                    />
                    {!readonly && columns.length > 1 && (
                      <button
                        type="button"
                        className="al-ex-del-btn al-meas-sec-coldel"
                        onClick={() => removeColumn(ci)}
                        title="Eliminar columna"
                      >
                        <Icon name="x" size={11} />
                      </button>
                    )}
                  </span>
                </th>
              ))}
              {!readonly && <th></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const rid = row.id || `idx${ri}`;
              return (
                <tr key={row.id || ri}>
                  <td className="al-meas-sec-rowh">
                    <b>
                      <InlineEditable
                        value={row.label}
                        onChange={(v) => updateRow(ri, { label: v })}
                        readonly={readonly}
                        placeholder="Medida…"
                        fieldPath={`row.${rid}.label`}
                      />
                    </b>
                    <span className="al-meas-sec-unit">
                      <InlineEditable
                        value={row.unit}
                        onChange={(v) => updateRow(ri, { unit: v })}
                        readonly={readonly}
                        placeholder="ud."
                        fieldPath={`row.${rid}.unit`}
                      />
                    </span>
                  </td>
                  {columns.map((_, ci) => (
                    <td key={ci}>
                      <InlineEditable
                        value={(row.values && row.values[ci]) || ""}
                        onChange={(v) => updateValue(ri, ci, v)}
                        readonly={readonly}
                        placeholder="—"
                        fieldPath={`row.${rid}.v.${ci}`}
                      />
                    </td>
                  ))}
                  {!readonly && (
                    <td>
                      <button
                        type="button"
                        className="al-ex-del-btn"
                        onClick={() => removeRow(ri)}
                        title="Eliminar fila"
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readonly && (
        <div className="al-meas-sec-actions">
          {clientId ? (
            <button type="button" className="al-day-add-ex" onClick={() => setImporting(true)}>
              <Icon name="download" size={12} /> Importar del cliente
            </button>
          ) : null}
          <button type="button" className="al-day-add-ex" onClick={addRow}>
            <Icon name="plus" size={12} /> Agregar fila
          </button>
          <button type="button" className="al-day-add-ex" onClick={addColumn}>
            <Icon name="plus" size={12} /> Agregar columna
          </button>
        </div>
      )}

      {importing && clientId ? (
        <MeasurementsImportModal
          clientId={clientId}
          onClose={() => setImporting(false)}
          onApply={(takes) => {
            const built = buildFromTakes(takes);
            if (built.rows.length > 0) onChange({ ...data, columns: built.columns, rows: built.rows });
            setImporting(false);
          }}
        />
      ) : null}
    </SectionPage>
  );
}

/** Modal: lista las tomas de medidas del cliente (con fecha) y deja elegir
 *  cuáles aplicar como columnas de la sección. */
function MeasurementsImportModal({
  clientId,
  onClose,
  onApply,
}: {
  clientId: number;
  onClose: () => void;
  onApply: (takes: ClientMeasurement[]) => void;
}) {
  const [takes, setTakes] = useState<ClientMeasurement[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    listMeasurements(clientId)
      .then((list) => {
        // Cronológico (más vieja primero) para que las columnas queden ordenadas.
        const ordered = [...list].sort((a, b) => (a.measured_at || "").localeCompare(b.measured_at || ""));
        setTakes(ordered);
        // Por defecto, seleccionamos las últimas 4 tomas.
        setSelected(new Set(ordered.slice(-4).map((m) => m.id)));
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [clientId]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const apply = () => {
    if (!takes) return;
    onApply(takes.filter((m) => selected.has(m.id)));
  };

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div className="al-modal" style={{ maxWidth: 460, width: "92vw" }} onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontSize: 16 }}>Importar medidas del cliente</h3>
          <button className="icon-btn" onClick={onClose} title="Cerrar"><Icon name="x" size={16} /></button>
        </div>
        <div style={{ padding: 16 }}>
          {err ? (
            <p className="tiny" style={{ color: "var(--danger, #dc2626)" }}>{err}</p>
          ) : !takes ? (
            <p className="tiny muted">Cargando tomas…</p>
          ) : takes.length === 0 ? (
            <p className="tiny muted">Este cliente todavía no tiene tomas de medidas registradas.</p>
          ) : (
            <>
              <p className="tiny muted" style={{ marginTop: 0 }}>
                Elegí qué tomas usar como columnas. Esto reemplaza la tabla actual con los datos reales del cliente.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto" }}>
                {takes.map((m) => (
                  <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
                    <span style={{ fontWeight: 600 }}>{(m.measured_at || "").slice(0, 10) || "Sin fecha"}</span>
                    <span className="tiny muted">
                      {[m.weight_kg != null ? `${m.weight_kg} kg` : null, m.waist_cm != null ? `cintura ${m.waist_cm}` : null].filter(Boolean).join(" · ")}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="al-modal-ft" style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: 16, paddingTop: 0 }}>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={apply} disabled={!takes || selected.size === 0}>
            Aplicar {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
